/**
 * The sim state (AD-007a): one plain, snapshottable, hashable structure holding
 * everything `step` needs. Config travels inside it deliberately — a snapshot
 * that cannot rebuild its own aircraft, tier and tunables is not a resumable
 * snapshot, and the regression/lockstep uses depend on resuming exactly.
 */

import { buildAircraftSpec, type AircraftSpec } from "./aircraft-spec.ts";
import type { AircraftKey } from "./aircraft-table.ts";
import { availableShaftPowerW, type EngineState } from "./engine-model.ts";
import { standardAtmosphere, type AirProperties } from "./atmosphere.ts";
import { loadedGuns, type AmmunitionType, type GunState } from "./guns.ts";
import { HEALTHY_PILOT, woundPilot, type PilotState } from "./physiology.ts";
import { UNDAMAGED, type DamageState } from "./damage.ts";
import { trimAlphaRad, trimThrottle } from "./performance.ts";
import { NO_STALL_MODES, type StallModeState } from "./spin.ts";
import { GEAR_AIRBORNE, GEAR_PARKED, type GearState } from "./undercarriage.ts";
import { createRngBankState, type RngBankState } from "./rng.ts";
import {
  BODY_FORWARD,
  ZERO_RATES,
  attitudeFromAngles,
  rotateVec3,
  scaleVec3,
  type BodyRates,
  type Quat,
  type Vec3,
} from "./math3d.ts";
import { ALL_REALISM_ON, type FlightModelTier, type RealismSettings } from "./tiers.ts";
import { makeTunables, type Tunables } from "./tunables.ts";
import { metresToFeet } from "./units.ts";
import { ZERO_WIND, type WeatherKind } from "./wind.ts";

/** Discrete things that happened during one tick, for HUD messages and tests. */
export const SIM_EVENT_KINDS = [
  "stallEntered",
  "stallRecovered",
  "spinEntered",
  "spinRecovered",
  "spiralEntered",
  "wingFailure",
  "gunJammed",
  "gunJamPermanent",
  "gunCleared",
  "carburettorIced",
  "carburettorThawed",
  "fuelExhausted",
  "hypoxiaDizzy",
  "hypoxiaBlackout",
  "hypoxiaRecovered",
  "pilotWounded",
  "bleedOutWarning",
  "pilotDied",
  "groundContact",
  "liftedOff",
  "gearCollapsed",
  "landed",
  "crashed",
] as const;

export type SimEventKind = (typeof SIM_EVENT_KINDS)[number];

/**
 * Semantic notes on two members, since both changed how they fire (or came
 * to exist) after the flight-scene lane had already built its own equivalent:
 *
 * - `groundContact` is now edge-triggered — it fires on the tick the aircraft
 *   *touches* the ground, not on every tick it spends resting there. Poll
 *   `readout.grounded` for the continuous state; a bounce or a takeoff roll
 *   that leaves and re-touches the ground fires the event again on the next
 *   touch.
 * - `pilotWounded` cannot be detected by `step`'s own previous/next diff the
 *   way `pilotDied` is: nothing in the per-tick physics ever sets
 *   `pilot.wounded` — only an external hit does, via `woundPilot`/`applyWound`
 *   — so there is no "previous tick" inside `step` to compare against. Use
 *   `applyWound(sim)` (below) instead of the bare `woundPilot(pilot, tunables)`
 *   to get this event stamped on the tick the wound actually lands.
 *   `bleedOutWarning`, by contrast, *is* a per-tick quantity (the countdown
 *   ticks inside `step` via `updatePilot`) and fires from ordinary
 *   previous/next comparison like every other event here.
 * - The three undercarriage events are all edge-triggered too. `liftedOff`
 *   fires once the wheels are clear of the ground by `gearReleaseClearanceM`;
 *   `gearCollapsed` on the tick the undercarriage folds (a hard arrival, a wing
 *   down, or the propeller into the ground); `landed` once a *safe* arrival has
 *   rolled to a stop, which is the transition the mission layer reads as "he is
 *   down and the sortie is over".
 */

export interface SimConfig {
  readonly aircraft: AircraftKey;
  readonly spec: AircraftSpec;
  readonly tier: FlightModelTier;
  readonly realism: RealismSettings;
  readonly tunables: Tunables;
  readonly seed: number;
  /**
   * AD-007c's altitude flag, default on: standard-atmosphere density plus the
   * fitted engine lapse. Off means sea-level air everywhere, which is the
   * comparison case for Open Question #3.
   */
  readonly altitudeLapse: boolean;
  /**
   * The mission's weather severity — byte `+6` of the original's scenario
   * header, which is what picks the `.WND` model and what `clouds.c` reads
   * (AD-007k). Part of the config rather than a per-tick input because the
   * original fixes it when the scenario is entered and never changes it in
   * flight; carrying it here is also what lets a lockstep peer agree on the
   * wind without exchanging anything.
   */
  readonly weather: WeatherKind;
  /**
   * The belt this sortie is flying — the mission's own AMMUNITION choice
   * (AD-011 §9.7's `desc[3]`, copied onto every aeroplane of the player's flight
   * at spawn).
   *
   * Config rather than a per-tick input for `weather`'s reasons, word for word:
   * the original fixes it when the scenario is entered and never changes it in
   * flight, and carrying it here is what lets a lockstep peer agree on the jam
   * rate without exchanging anything. The belt is *also* on `CombatAircraft`,
   * where the balloon-damage model reads it; both come from the one
   * `MissionFlight.ammunition`, and `combat-encounter.test.ts` asserts they
   * cannot disagree.
   */
  readonly ammunition: AmmunitionType;
}

/** Derived quantities the HUD, AI and tests read rather than recomputing. */
export interface AeroReadout {
  readonly airspeedMps: number;
  readonly alphaRad: number;
  readonly betaRad: number;
  readonly loadFactor: number;
  readonly stalled: boolean;
  /** SI altitude above datum, `position.y` restated here so a HUD never has to read `position` directly. */
  readonly altitudeM: number;
  /** `metresToFeet(altitudeM)`, kept for the imperial cockpit gauges (AD-007c) — derived, not independent. */
  readonly altitudeFt: number;
  /** Terrain height under the aircraft this tick, from the injected `GroundQuery` (`ground.ts`). Zero everywhere on flat ground. */
  readonly groundHeightM: number;
  /** Height of the wheels above the terrain: `altitudeM − groundHeightM − gearHeightM`. Zero on the ground, and what a landing is flown on. */
  readonly altitudeAglM: number;
  readonly climbRateMps: number;
  readonly bankRad: number;
  readonly turnLimited: boolean;
  /** True with the wheels on the surface. Continuous, unlike the edge-triggered `groundContact` event — for HUD/AI code that wants to poll grounded state every tick. */
  readonly grounded: boolean;
  /**
   * World-frame wind added to the flight path this tick, m/s (AD-007k). Zero
   * with REALISTIC WEATHER off and zero on the ground. Derived — a pure
   * function of altitude and config — and published here so a HUD drift
   * indicator and the tests can read it without re-deriving it.
   */
  readonly windMps: Vec3;
}

export interface SimState {
  readonly config: SimConfig;
  readonly tick: number;
  readonly timeS: number;
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly orientation: Quat;
  readonly rates: BodyRates;
  readonly engine: EngineState;
  readonly guns: GunState;
  readonly pilot: PilotState;
  readonly damage: DamageState;
  readonly stallModes: StallModeState;
  readonly gear: GearState;
  readonly readout: AeroReadout;
  readonly rng: RngBankState;
  readonly events: readonly SimEventKind[];
  readonly crashed: boolean;
}

export interface SimOptions {
  readonly aircraft: AircraftKey;
  readonly tier?: FlightModelTier;
  readonly realism?: Partial<RealismSettings>;
  readonly tunables?: Partial<Tunables>;
  readonly seed?: number;
  readonly altitudeM?: number;
  readonly airspeedMps?: number;
  readonly headingRad?: number;
  readonly throttle?: number;
  readonly altitudeLapse?: boolean;
  /**
   * Mission weather severity. Defaults to `still`, which is what both shipped
   * `SIM.IN` records carry in the scenario header's weather byte.
   */
  readonly weather?: WeatherKind;
  /** The mission's AMMUNITION choice. Defaults to ball, which is what every generator's default already is bar the two anti-gasbag strikes. */
  readonly ammunition?: AmmunitionType;
  /**
   * Solve trim angle of attack and throttle for level flight at the starting
   * speed/altitude (performance.ts's steady-state analysis), so a hands-off
   * start is genuine level cruise instead of settling into the small steady
   * descent an untrimmed 0°-pitch start produces. An explicit `throttle`
   * still wins over the solved trim throttle. Silently has no effect if the
   * requested speed cannot be trimmed level (e.g. below stall) — the flight
   * starts exactly as it would have without this option.
   */
  readonly trimmed?: boolean;
  /**
   * `PS.EXE`'s own START AT YOUR AERODROME (the sibling of START NEAR THE
   * ACTION): the flight begins with the wheels on the ground rather than in the
   * air. `altitudeM` is then read as the *terrain* height at the spawn, and the
   * aeroplane is placed on top of it at its three-point attitude, stationary
   * and throttled back, unless `airspeedMps`/`throttle` say otherwise.
   * Overrides `trimmed`, which has no meaning for an aeroplane that is not
   * flying.
   */
  readonly onGround?: boolean;
}

/** Fraction of top speed a flight starts at when the caller does not say. */
const DEFAULT_START_SPEED_FRACTION = 0.8;

export function createSimConfig(options: SimOptions): SimConfig {
  const tunables = makeTunables(options.tunables);
  return {
    aircraft: options.aircraft,
    spec: buildAircraftSpec(options.aircraft, tunables),
    tier: options.tier ?? "expert",
    realism: { ...ALL_REALISM_ON, ...options.realism },
    tunables,
    seed: options.seed ?? 1,
    altitudeLapse: options.altitudeLapse ?? true,
    weather: options.weather ?? "still",
    ammunition: options.ammunition ?? "regular",
  };
}

/** The starting pose: where the aeroplane is, which way it points, and how fast it is going. */
interface StartPose {
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly orientation: Quat;
  readonly alphaRad: number;
  readonly speedMps: number;
  readonly throttle: number;
  readonly gear: GearState;
  /** Terrain height under the spawn, which is `altitudeM` itself for a ground start. */
  readonly groundHeightM: number;
}

/** START AT YOUR AERODROME: on the wheels, at the three-point attitude, stationary and throttled back. */
function groundStart(
  options: SimOptions,
  spec: AircraftSpec,
  terrainM: number,
  wingIncidenceRad: number,
): StartPose {
  const headingRad = options.headingRad ?? 0;
  const speed = options.airspeedMps ?? 0;
  const orientation = attitudeFromAngles(headingRad, spec.undercarriage.groundPitchRad, 0);
  return {
    position: { x: 0, y: terrainM + spec.undercarriage.gearHeightM, z: 0 },
    velocity: scaleVec3(rotateVec3(attitudeFromAngles(headingRad, 0, 0), BODY_FORWARD), speed),
    orientation,
    // The wing sits at the tail-down attitude *plus* its incidence, which is
    // why a taildragger's wing is already working before the tail comes up.
    alphaRad: (speed > 0 ? spec.undercarriage.groundPitchRad : 0) + wingIncidenceRad,
    speedMps: speed,
    throttle: options.throttle ?? 0,
    gear: speed > 0 ? { onGround: true, collapsed: false, stopped: false } : GEAR_PARKED,
    groundHeightM: terrainM,
  };
}

function airborneStart(
  options: SimOptions,
  spec: AircraftSpec,
  air: AirProperties,
  altitudeM: number,
  wingIncidenceRad: number,
): StartPose {
  const speed = options.airspeedMps ?? spec.vmaxMps * DEFAULT_START_SPEED_FRACTION;
  const headingRad = options.headingRad ?? 0;
  const headingQuat = attitudeFromAngles(headingRad, 0, 0);
  const trimAlpha = options.trimmed ? trimAlphaRad(spec, air, speed) : Number.NaN;
  const trimApplied = Number.isFinite(trimAlpha);
  return {
    position: { x: 0, y: altitudeM, z: 0 },
    // Velocity is horizontal (the heading-only quaternion), independent of the
    // trimmed orientation below — that gap between where the nose points and
    // where the aircraft is going *is* the trim angle of attack.
    velocity: scaleVec3(rotateVec3(headingQuat, BODY_FORWARD), speed),
    // `trimAlphaRad` solves the angle **the wing** needs, so the fuselage sits
    // one incidence *below* it — which is how the original comes to fly level
    // with its nose 1.2° under the horizon.
    orientation: trimApplied
      ? attitudeFromAngles(headingRad, trimAlpha - wingIncidenceRad, 0)
      : headingQuat,
    alphaRad: trimApplied ? trimAlpha : wingIncidenceRad,
    speedMps: speed,
    throttle: options.throttle ?? (trimApplied ? trimThrottle(spec, air, speed) : 1),
    gear: GEAR_AIRBORNE,
    groundHeightM: 0,
  };
}

export function createSim(options: SimOptions): SimState {
  const config = createSimConfig(options);
  const { spec } = config;
  const altitudeM = options.altitudeM ?? 0;
  const air = standardAtmosphere(config.altitudeLapse ? altitudeM : 0);
  const incidence = config.tunables.wingIncidenceRad;
  const start = options.onGround
    ? groundStart(options, spec, altitudeM, incidence)
    : airborneStart(options, spec, air, altitudeM, incidence);

  return {
    config,
    tick: 0,
    timeS: 0,
    position: start.position,
    velocity: start.velocity,
    orientation: start.orientation,
    rates: ZERO_RATES,
    engine: {
      throttle: start.throttle,
      deliveredPowerW: availableShaftPowerW(spec.propulsion, air.densityRatio) * start.throttle,
      carbIced: false,
      carbThawSeconds: 0,
      fuelKg: spec.fuel.capacityKg,
      fuelExhausted: false,
    },
    guns: loadedGuns(spec.guns),
    pilot: HEALTHY_PILOT,
    damage: UNDAMAGED,
    stallModes: NO_STALL_MODES,
    gear: start.gear,
    readout: {
      airspeedMps: start.speedMps,
      alphaRad: start.alphaRad,
      betaRad: 0,
      loadFactor: 1,
      stalled: false,
      altitudeM: start.position.y,
      altitudeFt: metresToFeet(start.position.y),
      groundHeightM: start.groundHeightM,
      altitudeAglM: start.position.y - start.groundHeightM - spec.undercarriage.gearHeightM,
      climbRateMps: 0,
      bankRad: 0,
      turnLimited: false,
      grounded: start.gear.onGround,
      // A flight that has not been stepped yet has not been blown anywhere.
      windMps: ZERO_WIND,
    },
    rng: createRngBankState(config.seed),
    events: [],
    crashed: false,
  };
}

/**
 * Applies a wound to the pilot inside a full `SimState` and stamps
 * `pilotWounded` onto `events` the same tick — the sanctioned way for a
 * future combat/weapons lane to record a hit landing, in preference to the
 * bare `woundPilot(PilotState, Tunables)` (still exported, still the right
 * tool when only a `PilotState` is in hand). See the note above
 * `SimEventKind` for why `step` cannot detect this transition on its own.
 * Re-wounding an already-wounded pilot still runs the clock update (it can
 * only shorten the remaining time, never lengthen it) but does not repeat
 * the event.
 */
export function applyWound(sim: SimState): SimState {
  const alreadyWounded = sim.pilot.wounded;
  const pilot = woundPilot(sim.pilot, sim.config.tunables);
  if (alreadyWounded) {
    // Not a new wound: pass the pilot's shortened clock through, but strip
    // any `pilotWounded` still sitting in `events` from an earlier call this
    // same tick — the event marks the transition, not the wounded state.
    return { ...sim, pilot, events: sim.events.filter((kind) => kind !== "pilotWounded") };
  }
  return { ...sim, pilot, events: [...sim.events, "pilotWounded"] };
}

/**
 * The lethal counterpart of `applyWound`, for a round that goes through the
 * cockpit rather than beside it. `step` detects the *bleed-out* death by
 * comparing `pilot.alive` across a tick, but a hit lands from outside the tick,
 * so — exactly like `applyWound` — nothing inside `step` can see the
 * transition; this stamps `pilotDied` itself. Killing an already-dead pilot is
 * a no-op rather than a repeated event.
 */
export function killPilot(sim: SimState): SimState {
  if (!sim.pilot.alive) return sim;
  return {
    ...sim,
    pilot: { ...sim.pilot, alive: false },
    events: [...sim.events, "pilotDied"],
  };
}
